// Wellesley College: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Wellesley's Common Data Set 2025-2026 (Office of Institutional
//    Research; the PDF sits on Wellesley's file CDN, so the source link is
//    the Institutional Research Public Data page that links it): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy and score date, C21/C22 with early
//    decision counts for the Fall 2025 entering class (both rounds
//    together). Its section G marks the 2026-2027 costs as not available,
//    so costs come from Student Financial Services' Resident Student Cost
//    of Attendance 2026-2027.
//  - wellesley.edu admissions: Apply (the testing statement for fall 2027
//    entry) and First-Year Applicants (dates and deadlines table, Early
//    Decision agreement).
//  - College Scorecard API, school id 168218 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Wellesley Common Data Set 2025-2026',
        url: 'https://www.wellesley.edu/about-us/offices-departments/office-of-institutional-research/public-data',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Wellesley Admission, First-Year Applicants',
        url: 'https://www.wellesley.edu/admission-aid/apply/first-year-applicants',
        read: '2026-10-01',
    },
    apply: {
        label: 'Wellesley Admission, Apply',
        url: 'https://www.wellesley.edu/admission-aid/apply',
        read: '2026-10-01',
    },
    coa: {
        label: 'Wellesley Student Financial Services, Tuition and Cost 2026-2027',
        url: 'https://www.wellesley.edu/admission-aid/student-financial-services/tuition-cost',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?168218-Wellesley-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'wellesley-college',
    kbId: 'wellesley_college',
    name: 'Wellesley College',
    shortName: 'Wellesley',
    location: 'Wellesley, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Wellesley visual identity guide); decoration only.
    branding: {
        primary: '#0142A4',
        secondary: '#0C2142',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/wellesley_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8506,
        admitted: 1258,
        enrolled: 576,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 698, admitted: 151 },
            { group: 'Out-of-state', applied: 4676, admitted: 1000 },
            { group: 'International', applied: 3132, admitted: 107 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Wellesley\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Counts for both early decision rounds together. Wellesley has no early action plan.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I and II', applied: 1023, admitted: 301 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 8' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 8', decision: 'Late March', binding: false },
        ],
        note: 'Financial aid applications are due on the same dates. The reply and deposit date is early January for Early Decision I, late February for Early Decision II and May 1 for Regular Decision. Arts portfolios, recordings and presentations go through the Wellesley Applicant Portal within one week of the application deadline. Wellesley is also a QuestBridge partner.',
        source: 'firstYear',
        binding: 'Early Decision is for students who know Wellesley is their best fit: you, a parent or guardian and a school counselor sign the Early Decision Agreement.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Wellesley will continue to be test-optional for students applying for entry in fall 2027; about 45% of its enrolling first-year students applied without testing. Whether or not you send scores, the committee looks closely at your writing and quantitative skills.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Wellesley calls its policy a suspension of its testing requirement for 2026-2027 and says it will share updates for future cycles. It has not said whether students entering in fall 2028 must send SAT or ACT scores.', source: 'apply' },
        ],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72570 },
            { item: 'Housing', amount: 12020 },
            { item: 'Meals', amount: 11186 },
            { item: 'Student activity fee', amount: 360 },
            { item: 'Books', amount: 800 },
            { item: 'Personal expenses', amount: 1250 },
            { item: 'Transportation', amount: null },
        ],
        total: 98186,
        note: 'For a resident student. Wellesley lists the items without a total; $98,186 is their sum. Transportation varies with distance from home, so it is not included, and students without qualifying health insurance also pay the health insurance fee.',
        source: 'coa',
        netPrice: {
            average: 23995,
            byIncome: [
                { income: '$0 to $30,000', amount: 11253 },
                { income: '$30,001 to $48,000', amount: 2791 },
                { income: '$48,001 to $75,000', amount: 5535 },
                { income: '$75,001 to $110,000', amount: 12053 },
                { income: 'Over $110,000', amount: 46438 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Wellesley\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
